<template>
  <ModalDialog
    ref="dialog"
    title="Import Order Items"
    icon="fa-file-import"
    sizeClass='modal-xl'
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    submitTitle="Import"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="importForm" :labelColumnSize="3">
      <div v-if="!hasFiles && !processing && !loading">
        <FormUploadDropTarget title="Drop CSV files here to import" @filesDropped="onFilesDropped">        
          <ContentTitle>Import CSV File</ContentTitle>
          <FormRow label="CSV File">
            <template v-slot:label>
              <InlineHelpPopover title="CSV Import" placement="right">
                <p>Importing allows importing order items directly from a CSV file. For best results, use the following guidelines:</p>
                <ul>
                  <li>CSV files <strong>must</strong> have a header line at top describing the columns</li>
                  <li>Stock and Price group names must be as close as possible to the Synergy items names to match up with data in Synergy</li>
                  <li>All service based stock items following a glass item will be associated with the last glass item</li>
                  <li>See the CSV import guidelines for more details and examples</li>
                </ul>
              </InlineHelpPopover>
            </template>
            <FormUpload :singleFile="true" :fileTypes="['csv']" :autoUpload="false" @fileSelected="onFilesDropped" />
            <div class="mt-2">
              <a href="/documents/Synsoft-CSV-Import-Guidelines-v1.0.pdf" target="_blank">CSV Import Guidelines <i class="far fa-external-link"></i></a>
            </div>
          </FormRow>
        </FormUploadDropTarget>
      </div>

      <LoadingPanel :loading="processing || loading" :loadingError="loadingError" />
      <div class="text-center text-muted mt-4" v-if="processing">Importing data...</div>
      <div class="text-center text-muted mt-4" v-if="loading">Loading...</div>

      <div v-if="hasImportedItems && !processing">
        <Scroller :maxHeight="350">
          <StaticDataTable class="static-table-list">
            <template v-slot:header>
              <tr>
                <th>Item</th>
                <th v-for="fieldInfo of fieldList" :key="fieldInfo.name">
                  <template v-if="columnHasMappingIssue(fieldInfo.name)">
                    <Tooltip :label="`Column '${fieldInfo.name}' not found in CSV file`">
                      <a href="#" class="order-item-editable-link" @click.prevent="columnClicked($event, fieldInfo)">{{fieldInfo.name}}</a> <i class="far fa-exclamation-triangle text-danger"></i>
                    </Tooltip>
                  </template>
                  <template v-if="!columnHasMappingIssue(fieldInfo.name)">
                    {{fieldInfo.name}}
                  </template>
                </th>
              </tr>
            </template>
            <template v-slot:body>
              <tr v-for="(itemInfo, index) of importedItemList" :key="index">
                <td>
                  <span v-if="!itemInfo.isChildItem">{{itemInfo.orderItemIndex + 1}}</span>
                </td>
                <td v-for="(fieldInfo, fieldIndex) of fieldList" :key="fieldInfo.name">
                  <span v-if="fieldIndex == 0 && itemInfo.isChildItem">
                    &nbsp;&nbsp;<i class="fas fa-caret-right fa-sm text-muted"></i>&nbsp;&nbsp;
                  </span>
                  {{itemInfo.data[fieldInfo.name].displayValue}}
                  <Tooltip v-if="itemInfo.data[fieldInfo.name].error" :label="itemInfo.data[fieldInfo.name].error">
                    <i class="far fa-exclamation-triangle text-danger"></i>
                  </Tooltip>
                </td>
              </tr>
            </template>
            <template v-slot:empty v-if="importedItemList.length == 0">No valid items found</template>
          </StaticDataTable>
        </Scroller>

        <Popover ref="columnMappingPopover" sizeClass="popover-large popover-no-padding" :showHeader="true" headerTitle="Column Mapping">
          <div class="p-2">
            <FormRow label="Column" :labelColumnSize="4">
              <FormStaticLabel>{{mappingUpdateInfo.requiredFieldName}}</FormStaticLabel>
            </FormRow>
            <FormRow label="CSV Field" :labelColumnSize="4">
              <FormStaticLabel>
                <FormSelect :options="importedSourceFieldLookup" v-model="mappingUpdateInfo.sourceDataFieldName" />
              </FormStaticLabel>
            </FormRow>

            <div class="mt-3 text-right">
              <FormButton class="btn-primary btn-sm" @click="applyMapping">Apply</FormButton>
            </div>
          </div>
        </Popover>

      </div>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import Popover from '@/components/popover.vue'

import { CSVReader } from '@/utils/csvUtils'
import Utils from '@/utils/utils'

import { InventoryStockTypes, UOMTypes, OrderItemEdgeworkTypes, AccountSettingKeys } from '@/store/types'

export default {
  name: 'OrderItemImportDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    Popover
  },
  props: {
    
  },
  data() {
    return {
      hasFiles: false,
      processing: false,
      loading: false,
      loadingError: '',
      submitting: false,
      stockList: [],
      priceGroupList: [],
      customFieldList: [],

      mappingUpdateInfo: {
        requiredFieldName: '',
        sourceDataFieldName: ''
      },

      fieldList: [
        { name: 'Stock' },
        { name: 'Price Group' },
        { name: 'Quantity', type: 'number' },
        { name: 'Height', type: 'number' },
        { name: 'Width', type: 'number' },
        { name: 'Mark' },
        { name: 'Notes' },
        { name: 'Edgework' },
        { name: 'Custom Fields', optional: true }
      ],
      importedColumnMapping: {},
      importedSourceData: [],
      importedSourceFieldList: [],
      importedItemList: [],

      importForm: {}
    }
  },
  mounted() {
    // Load the stock / config data
    this.requestData()
  },
  computed: {
    hasImportedItems() {
      return this.importedItemList.length > 0
    },
    submitDisabled() {
      return this.loading || this.processing || this.importedItemList.length == 0
    }    
  },
  methods: {
    show(params) {
      this.importForm = {}
      return modalDialog.methods.show.call(this, params)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.importForm = {}

      // Generate the order item list
      var outputOrderItems = this.importedItemList.map(orderItem => {
        return {
          id: orderItem.id,
          parentOrderItemId: orderItem.parentOrderItemId,
          stockType: orderItem.stockType,
          stockId: orderItem.outputData.stockId,
          stockPriceGroupId: orderItem.outputData.stockPriceGroupId,
          description: orderItem.outputData.description,
          pricingDescription: orderItem.outputData.description,
          quantity: orderItem.outputData.quantity,
          units: orderItem.outputData.units,
          unitsOverridden: !!orderItem.outputData.unitsOverridden,
          glassHeight: orderItem.outputData.height,
          glassWidth: orderItem.outputData.width,
          mark: orderItem.outputData.mark,
          notes: orderItem.outputData.notes,
          edgeworkFlagList: orderItem.outputData.edgeworkFlagList,
          glassLevelId: orderItem.outputData.glassLevelId,
          customFieldData: orderItem.outputData.customFieldData,

          stock: orderItem.outputData.stock,
          glassLevel: orderItem.outputData.glassLevel,
          stockPriceGroup: orderItem.outputData.stockPriceGroup
        }
      })

      this.modalClosed(outputOrderItems)
      this.hide()
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.inventoryService.getStockList({}, { isActive: 1 }),
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.adminService.getAccountSettings()
      ])
      .then(results => {
        this.stockList = results[0].data
        this.priceGroupList = results[1].data.filter(priceGroup => priceGroup.isActive)

        try {
          let accountSettings = results[2].data
          this.customFieldList = JSON.parse(accountSettings[AccountSettingKeys.orderCustomFieldList] || [])
        } catch(err) {
          this.customFieldList = []
        }
      })
      .catch(err => {
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })    
    },
    onFilesDropped(files) {
      if(files.length == 0)
        return

      let file = files[0]
      this.importForm = {}

      this.importError = ''
      this.processing = true
      this.importedItemList = []
      this.importedSourceFieldLookup = []

      this.$nextTick(() => {
        let dialogContext = this

        // Read & process the DXF file
        var reader = new FileReader();
        reader.onload = (function(theFile) {
          return function(e) {

            try {
              var reader = new CSVReader()
              reader.import(e.target.result)

              dialogContext.importedSourceData = reader.rowData
              dialogContext.importedSourceFieldLookup = reader.columnList.map(field => {
                return {
                  name: field,
                  value: field
                }
              })
              
              // Find best mapping for column list
              dialogContext.importedColumnMapping = Utils.calculateDataMappingMatches(dialogContext.fieldList.map(fieldInfo => fieldInfo.name), reader.columnList, 0.7)

              // Calculate imported data this
              dialogContext.recalculateImportData()

              dialogContext.hasFiles = true
              
            } catch(err) {
              console.error(err)

              dialogContext.processing = false
              dialogContext.importForm.errorMessage = 'Error importing file. Ensure the file is a valid CSV file'
            }
          }
        })(file)

        reader.readAsText(file);
      })
    },
    columnHasMappingIssue(fieldName) {
      var fieldInfo = this.fieldList.find(fieldInfo => fieldInfo.name == fieldName)
      if(fieldInfo) {
        if(fieldInfo.optional)
          return false
      }
      return !this.importedColumnMapping[fieldName]
    },
    async recalculateImportData() {
      var edgeworkFlags = [
        { name: 'longa', value: OrderItemEdgeworkTypes.longA, displayValue: 'Long A' },
        { name: 'longb', value: OrderItemEdgeworkTypes.longB, displayValue: 'Long B' },
        { name: 'shorta', value: OrderItemEdgeworkTypes.shortA, displayValue: 'Short A' },
        { name: 'shortb', value: OrderItemEdgeworkTypes.shortB, displayValue: 'Short B' },
        { name: 'all', value: OrderItemEdgeworkTypes.allRound, displayValue: 'All Round' }
      ]

      this.importedItemList = []
      
      this.importedSourceData.forEach((srcRow, index) => {
        var outputItem = {
          id: `temp-id-` + uuidv4(),
          parentOrderItemId: null,
          stockType: '',
          orderItemIndex: 0,
          isChildItem: false,
          sourceRow: srcRow,
          data: {},
          outputData: {
            stockId: 0,
            stockPriceGroupId: 0,
            quantity: 0,
            height: 0,
            width: 0,
            mark: '',
            notes: '',
            edgeworkFlagList: [],
            stock: {},
            glassLevel: {},
            stockPriceGroup: {},
            customFieldData: []
          }
        }

        this.fieldList.forEach(fieldInfo => {
          let displayValue = ('' + (srcRow[this.importedColumnMapping[fieldInfo.name]] || '')).trim()
          let srcData = displayValue

          if(fieldInfo.type == 'number')
            srcData = parseFloat(srcData || 0)

          outputItem.data[fieldInfo.name] = {
            field: fieldInfo.name,
            value: srcData,
            displayValue: displayValue,
            error: '',
            id: null
          }
        })

        // If all fields empty, ignore the row
        if(this.fieldList.every(fieldInfo => _.isEmpty(outputItem.data[fieldInfo.name].value)))
          return

        this.importedItemList.push(outputItem)
      })

      // Search stock / price groups & flag any errors
      // Process values
      var parentItemIndex = 0
      var prevGlassItemId = null
      for(let item of this.importedItemList) {
        try {
          let stockName = item.data['Stock'].value
          let priceGroupName = item.data['Price Group'].value
          let edgeValue = item.data['Edgework'].value

          let stockItem = Utils.calculateClosestMatchingItem(stockName, this.stockList, 'name', 0.85)
          let pgItem = Utils.calculateClosestMatchingItem(priceGroupName, this.priceGroupList, 'name', 0.85)

          item.outputData.units = 0

          if(stockItem) {
            item.stockType = stockItem.type

            item.data['Stock'].id = stockItem.id
            item.data['Stock'].value = stockItem.name
            item.data['Stock'].displayValue = stockItem.name

            item.outputData.stockId = stockItem.id
            item.outputData.stock = stockItem
            item.outputData.description = stockItem.name
          } else {
            item.data['Stock'].error = 'Stock item not found'
          }

          if(pgItem) {
            item.data['Price Group'].id = pgItem.id
            item.data['Price Group'].value = pgItem.name
            item.data['Price Group'].displayValue = pgItem.name

            item.outputData.stockPriceGroup = pgItem
            item.outputData.stockPriceGroupId = pgItem.id
          } else if(priceGroupName != '') {
            item.data['Price Group'].error = 'Price group not found'
          }

          if(item.stockType == InventoryStockTypes.glass) {
            item.outputData.width = item.data['Width'].value
            item.outputData.height = item.data['Height'].value
            item.outputData.mark = item.data['Mark'].value
            item.outputData.notes = item.data['Notes'].value
            item.outputData.quantity = parseInt(item.data['Quantity'].value)

            // Find GlassLevelId by size if stock linked pricing group
            if(pgItem && pgItem.isStockLinked) {
              let response = await this.$apiService.orderItemService.getStock(stockItem.id, { batchRequest: true })
              let stockInfo = response.data

              let glassLevel = null
              if(stockInfo.glassSizeList) {
                stockInfo.glassSizeList.forEach(glassSize => {
                  if(glassSize.glassLevels) {
                    let level = glassSize.glassLevels.find(glassLevel => glassLevel.width == item.outputData.width && glassLevel.height == item.outputData.height)
                    if(level)
                      glassLevel = level
                  }
                })
              }

              if(glassLevel) {
                item.outputData.glassLevel = glassLevel
                item.outputData.glassLevelId = glassLevel.id
              } else {
                item.data['Height'].error = `Stock sheet for size ${item.outputData.height}x${item.outputData.width} not found`
              }
            }

            // Custom fields on glass items
            item.outputData.customFieldData = []

            this.customFieldList.forEach(customFieldName => {
              let field = ('' + customFieldName).trim().toLowerCase()
              let sourceField = Object.keys(item.sourceRow).find(srcField => ('' + srcField).toLowerCase() == field)
              if(sourceField) {
                item.outputData.customFieldData.push({
                  key: customFieldName,
                  value: item.sourceRow[sourceField]
                })
              }
            })

            item.data['Custom Fields'].displayValue = (item.outputData.customFieldData || []).map(customField => {
              return `${customField.key}: ${customField.value}`
            }).join(', ')

            prevGlassItemId = item.id
          }

          if(stockItem && item.stockType == InventoryStockTypes.glassService) {
            item.isChildItem = true
            item.parentOrderItemId = prevGlassItemId

            // Check edgework flags
            if(stockItem.uomType == UOMTypes.length && !_.isEmpty(edgeValue)) {
              let edgeItems = edgeValue.split(',')
              let outputEdgeFlags = _.compact(edgeItems.map(edgeFlag => {
                var foundEdgeFlag = Utils.calculateClosestMatchingItem(edgeFlag, edgeworkFlags, 'name', 0.85)
                return foundEdgeFlag
              }))

              if(outputEdgeFlags.length != edgeItems.length) {
                item.data['Edgework'].error = 'Invalid edgework flags'
              } else {
                item.data['Edgework'].value = outputEdgeFlags.map(edge => edge.value)
                item.data['Edgework'].displayValue = outputEdgeFlags.map(edge => edge.displayValue).join(', ')
                item.outputData.edgeworkFlagList = outputEdgeFlags.map(edge => edge.value)

                // If all round, flag all 4 edges
                if(item.outputData.edgeworkFlagList.some(edgeFlag => edgeFlag == OrderItemEdgeworkTypes.allRound)) {
                  item.outputData.edgeworkFlagList = [OrderItemEdgeworkTypes.shortA, OrderItemEdgeworkTypes.shortB, OrderItemEdgeworkTypes.longA, OrderItemEdgeworkTypes.longB]
                }
              }
            } else if(stockItem.uomType == UOMTypes.each) {
              item.outputData.units = parseInt(item.data['Quantity'].value) || 1
              if(item.outputData.units != 1)
                item.outputData.unitsOverridden = true
            }

            item.outputData.quantity = 1

          } else {
            item.orderItemIndex = parentItemIndex++
          }
        } catch(err) {
          console.error(err)
        }
      }

      this.processing = false
    },
    columnClicked(ev, columnInfo) {
      this.$refs.columnMappingPopover.displayFromElement(ev.target)

      this.mappingUpdateInfo.requiredFieldName = columnInfo.name
      this.mappingUpdateInfo.sourceDataFieldName = ''
    },
    applyMapping() {
      this.$refs.columnMappingPopover.hidePopover()

      // Update mapping & try processing imported data again
      this.importedColumnMapping[this.mappingUpdateInfo.requiredFieldName] = this.mappingUpdateInfo.sourceDataFieldName      

      this.recalculateImportData()
    }
  }
}
</script>